import { Box, Button, Paper, Stack, TableContainer, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CreateCustomerFormDialog } from '~/components/Dialog/CustomerFormDialog';
import { Search } from '~/components/Search';
import { TablePagination } from '~/components/Table';
import { usePagination } from '~/hooks/usePagination';
import { useTableSorting } from '~/hooks/useTableSorting';
import { useCustomers } from '~/models/Customer/hooks';
import { AmplitudeEvent } from '~/types/amplitude';
import { Customer } from '~/types/customer';
import { SortDirection } from '~/types/table';
import { sendAmplitudeData } from '~/utils/amplitude';

import { CustomersTable } from './CustomersTable';

export const Customers = () => {
  const { t } = useTranslation();

  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(0);

  const { ordering, currentSort, sortDirection, onSort } = useTableSorting<Customer>(
    'id',
    SortDirection.Desc,
  );

  const { offset, page, rowsPerPage, onPageChange, onRowsPerPageChange } = usePagination(count);

  const { data, isLoading } = useCustomers({
    offset,
    limit: rowsPerPage,
    ordering,
    search,
    library: 'private',
  });

  const onSearch = (searchTerm: string) => {
    if (searchTerm === search) return;

    setSearch(searchTerm);
    onPageChange(1);
  };

  useEffect(() => {
    sendAmplitudeData(AmplitudeEvent.CustomerListRefreshed);
  }, []);

  useEffect(() => {
    if (data) {
      setCount(data.count);
    }
  }, [data]);

  const handleAddNewCustomer = () => {
    sendAmplitudeData(AmplitudeEvent.CustomerNewStarted);
    setShowAddCustomerDialog(true);
  };

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          mb={1}
        >
          <Typography variant="h5">{t('msg_customers_title')}</Typography>
          <Button variant="contained" color="primary" size="large" onClick={handleAddNewCustomer}>
            {t('msg_label_add_new_customer')}
          </Button>
        </Stack>

        <Box>
          <Search onSearch={onSearch} />
        </Box>

        <Paper>
          <TableContainer sx={{ position: 'relative' }}>
            <CustomersTable
              items={data?.items}
              onSort={onSort}
              currentSort={currentSort}
              sortDirection={sortDirection}
              isLoading={isLoading}
            />
            <TablePagination
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </TableContainer>
        </Paper>
      </Stack>

      <CreateCustomerFormDialog
        open={showAddCustomerDialog}
        onClose={() => setShowAddCustomerDialog(false)}
      />
    </>
  );
};
